import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, ConfigProvider, Empty, Pagination, Spin, Statistic, Table, Tag, theme } from 'antd';
import { Page } from 'components/page/page';
import { getViaAuth, postViaAuth } from 'apis/api';
import { printReport } from './print-report';
import './financial-reports.scss';

interface Row { section: string; source: string; symbol: string; amount: number | null; usdtValue: number | null; observedAt: string; error?: string; }
interface Report { reportId: string; source: string; startedAt: string; completedAt: string; usdtSellPrice: number | null; assetsUsdt: number | null; liabilitiesUsdt: number | null; pendingUsdt: number | null; netUsdt: number | null; netAfterPendingUsdt: number | null; rows: Row[]; }
interface History { total: number; items: Report[]; }
const amount = (value: number | null) => value == null ? 'نامشخص' : value.toLocaleString('fa-IR', { maximumFractionDigits: 8 });
const date = (value: string) => new Date(value).toLocaleString('fa-IR', { timeZone: 'Asia/Tehran' });
export default function BalanceReports() {
    const [history, setHistory] = useState<History>({ total: 0, items: [] });
    const [report, setReport] = useState<Report>();
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);
    const generation = useRef(false);
    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        void getViaAuth<History>('Admin/Dashboard/BalanceReports', { page }).then(response => {
            if (!active) return;
            if (!response.value || response.status < 200 || response.status >= 300) throw new Error();
            setHistory(response.value);
            setReport(current => current || response.value.items[0]);
        }).catch(() => { if (active) setError('دریافت سوابق ناموفق بود؛ دوباره تلاش کنید.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [page, reload]);
    const generate = async () => {
        if (generation.current) return;
        generation.current = true;
        setGenerating(true); setError('');
        try {
            const response = await postViaAuth<Report>('Admin/Dashboard/BalanceReports', {});
            if (!response.value || response.status < 200 || response.status >= 300) throw new Error();
            setReport(response.value); setPage(1); setReload(x => x + 1);
        } catch { setError('ساخت گزارش موجودی ناموفق بود؛ دوباره تلاش کنید.'); }
        finally { setGenerating(false); generation.current = false; }
    };
    return <ConfigProvider direction="rtl" theme={{ algorithm: theme.darkAlgorithm }}><Page title="گزارش موجودی" className="financial-reports">
        <Card title="گزارش موجودی و تعهدات PMXChange">
            <p>موجودی ریالی فقط از پی‌استار دریافت می‌شود. دارایی‌ها، بدهی کاربران و برداشت‌های در انتظار به تفکیک منبع ثبت می‌شوند.</p>
            <Button type="primary" loading={generating} onClick={() => void generate()}>دریافت و ذخیره گزارش جدید</Button>
            <Button disabled={loading || generating} onClick={() => setReload(x => x + 1)}>به‌روزرسانی سوابق</Button>
            <Button disabled={!report} onClick={() => printReport('pmx-balance-report', 'گزارش موجودی PMXChange')}>چاپ / ذخیره PDF</Button>
            {error && <Alert type="error" message={error} showIcon />}
            {generating && <Alert type="info" message="در حال دریافت منابع مالی؛ این صفحه را تا پایان باز نگه دارید." />}
        </Card>
        <div id="pmx-balance-report" className="financial-report-content" style={{ marginTop: 20 }}>
            {!report ? <Empty description="هنوز گزارشی ثبت نشده است." /> : <>
                <Card title="گزارش موجودی PMXChange">
                    <p>{date(report.startedAt)} تا {date(report.completedAt)} <Tag>{report.source === 'scheduled' ? 'شبانه' : 'دستی'}</Tag></p>
                    <p>نرخ تبدیل تتر: {amount(report.usdtSellPrice)} تومان</p>
                    {report.rows.some(x => x.error) && <Alert type="warning" showIcon message="برخی منابع یا نرخ‌ها نامشخص‌اند؛ جمع وابسته به آن‌ها نیز نامشخص است." />}
                    <div className="report-metrics">{[
                        ['مجموع دارایی', report.assetsUsdt], ['بدهی به کاربران', report.liabilitiesUsdt],
                        ['خالص دارایی', report.netUsdt], ['برداشت‌های در انتظار', report.pendingUsdt],
                        ['خالص پس از برداشت‌های در انتظار', report.netAfterPendingUsdt],
                    ].map(([title, value]) => <Card key={String(title)}><Statistic title={title} value={amount(value as number | null)} suffix="USDT" /></Card>)}</div>
                    <p className="report-caption">خالص دارایی برابر دارایی منهای موجودی کاربران است. برداشت‌هایی که از کیف پول کسر شده‌اند جداگانه از خالص کم می‌شوند. زمان دریافت منابع یکسان نیست و بازه ثبت در بالا مشخص است.</p>
                </Card>
                {([['assets', 'دارایی منابع مالی'], ['liabilities', 'بدهی کیف پول کاربران'], ['pending', 'برداشت‌های در انتظار']] as const).map(([section, title]) =>
                    <Card key={section} title={title}><Table<Row> dataSource={report.rows.filter(x => x.section === section)} pagination={false}
                        rowKey={row => `${row.source}:${row.symbol}`} scroll={{ x: 850 }} columns={[
                            { title: 'منبع', dataIndex: 'source' }, { title: 'ارز', dataIndex: 'symbol' },
                            { title: 'مقدار (IRT: تومان)', dataIndex: 'amount', render: amount },
                            { title: 'معادل تتر', dataIndex: 'usdtValue', render: amount },
                            { title: 'زمان دریافت', dataIndex: 'observedAt', render: date },
                            { title: 'وضعیت', dataIndex: 'error', render: value => value || 'کامل' },
                        ]} /></Card>)}
            </>}
        </div>
        <Card title="سوابق گزارش‌های ذخیره‌شده" style={{ marginTop: 20 }}><Spin spinning={loading}>
            <Table<Report> dataSource={history.items} rowKey="reportId" pagination={false} scroll={{ x: 650 }} columns={[
                { title: 'زمان ثبت (تهران)', dataIndex: 'startedAt', render: date },
                { title: 'نوع', dataIndex: 'source', render: value => value === 'scheduled' ? 'شبانه' : 'دستی' },
                { title: 'خالص (تتر)', dataIndex: 'netUsdt', render: amount },
                { title: 'مشاهده', render: (_, row) => <Button onClick={() => setReport(row)}>باز کردن گزارش</Button> },
            ]} /><Pagination current={page} total={history.total} pageSize={20} showSizeChanger={false} onChange={setPage} />
        </Spin></Card>
    </Page></ConfigProvider>;
}
